import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { GraduationCap, Plus, BookOpen, FileText, ClipboardList, Upload, Trash2, Eye, EyeOff, Save, ChevronRight, Film, ListChecks, Users, Search, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminCourses — trainer-facing LMS. Create courses, add lessons
 * (video + PDF + body text), add worksheets (fillable or PDF-upload),
 * and review student submissions. Minimalist MVP meant to grow.
 */
export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newCourse, setNewCourse] = useState({ title: '', description: '', price: 0, published: true });
  const [detail, setDetail] = useState(null);

  const load = () => api.get('/courses', { params: { include_drafts: true } }).then(r => { setCourses(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const createCourse = async () => {
    if (!newCourse.title.trim()) { toast.error('Title required'); return; }
    try { await api.post('/admin/courses', newCourse); setAddOpen(false); setNewCourse({ title:'', description:'', price:0, published:true }); load(); toast.success('Course created'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggleCourse = async (c) => {
    try { await api.patch(`/admin/courses/${c.id}`, { published: !c.published }); load(); }
    catch { toast.error('Toggle failed'); }
  };

  const archiveCourse = async (id) => {
    if (!window.confirm('Archive this course?')) return;
    try { await api.delete(`/admin/courses/${id}`); load(); toast.success('Archived'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto' data-testid='admin-courses-page'>
      <div className='flex items-start justify-between mb-6 flex-wrap gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Learn</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>Online Courses</h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>Build curriculum, upload worksheets, review student submissions.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className='bg-[#C8102E] hover:bg-[#A50D26] text-white font-semibold' data-testid='add-course-btn'>
          <Plus className='w-4 h-4 mr-2' /> New course
        </Button>
      </div>

      {loading ? <div className='text-slate-400 text-sm'>Loading…</div> : (
        courses.length === 0 ? (
          <Card className='p-10 text-center text-slate-500 dark:text-slate-400'>
            <GraduationCap className='w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600' />
            No courses yet. Click “New course” to get started.
          </Card>
        ) : (
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {courses.map(c => (
              <Card key={c.id} className='p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow flex flex-col' data-testid={`course-card-${c.id}`}>
                <div className='flex items-start justify-between gap-2 mb-2'>
                  <h3 className='font-display font-semibold text-slate-900 dark:text-slate-50 leading-tight'>{c.title}</h3>
                  <Badge className={c.published ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}>
                    {c.published ? 'Live' : 'Draft'}
                  </Badge>
                </div>
                <p className='text-xs text-slate-500 dark:text-slate-400 line-clamp-3 mb-2'>{c.description || 'No description yet.'}</p>
                <div className='flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-3'>
                  <BookOpen className='w-3.5 h-3.5' /> {c.lesson_count || 0} lessons
                  <span className='mx-1'>&middot;</span>
                  <span className='font-semibold text-slate-700 dark:text-slate-200'>{c.price > 0 ? `$${c.price}` : 'Free'}</span>
                </div>
                <div className='mt-auto flex items-center gap-2 flex-wrap'>
                  <Button size='sm' onClick={() => setDetail(c)} className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid={`course-open-${c.id}`}>
                    Manage <ChevronRight className='w-3.5 h-3.5 ml-1' />
                  </Button>
                  <Button size='sm' variant='outline' onClick={() => toggleCourse(c)} className='text-xs' data-testid={`course-toggle-${c.id}`}>
                    {c.published ? <><EyeOff className='w-3.5 h-3.5 mr-1' /> Unpublish</> : <><Eye className='w-3.5 h-3.5 mr-1' /> Publish</>}
                  </Button>
                  <Button size='sm' variant='ghost' onClick={() => archiveCourse(c.id)} className='text-[#B45309] hover:bg-[#B45309]/5' data-testid={`course-archive-${c.id}`}>
                    <Trash2 className='w-3.5 h-3.5' />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {/* New course dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className='max-w-md' data-testid='add-course-dialog'>
          <DialogHeader><DialogTitle>Create course</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div><Label>Title *</Label><Input value={newCourse.title} onChange={e => setNewCourse({ ...newCourse, title: e.target.value })} placeholder='Dog Obedience 101' data-testid='nc-title' /></div>
            <div><Label>Description</Label><Textarea rows={3} value={newCourse.description} onChange={e => setNewCourse({ ...newCourse, description: e.target.value })} data-testid='nc-desc' /></div>
            <div className='grid grid-cols-2 gap-3'>
              <div><Label>Price ($)</Label><Input type='number' min='0' value={newCourse.price} onChange={e => setNewCourse({ ...newCourse, price: Number(e.target.value || 0) })} data-testid='nc-price' /></div>
              <div className='flex items-end'><label className='flex items-center gap-2 text-sm'><input type='checkbox' checked={newCourse.published} onChange={e => setNewCourse({ ...newCourse, published: e.target.checked })} /> Publish now</label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createCourse} className='bg-[#C8102E] hover:bg-[#A50D26] text-white' data-testid='nc-submit'>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / edit dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto' data-testid='course-detail-dialog'>
          {detail && <CourseDetail courseId={detail.id} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------- Course detail: lessons + worksheets editor ------- */
const CourseDetail = ({ courseId, onClose }) => {
  const [data, setData] = useState(null);
  const [lessonForm, setLessonForm] = useState({ title: '', body: '', video_url: '', attachment_url: '', attachment_name: '' });
  const [wsForm, setWsForm] = useState({ title: '', kind: 'fillable', pdf_url: '', questions: [{ id: 'q1', label: '', type: 'text', required: true }] });
  const load = () => api.get(`/courses/${courseId}`).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const uploadFile = async (purpose, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('purpose', purpose);
    const { data: up } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return up;
  };

  const onLessonVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await uploadFile('client_media', f); setLessonForm({ ...lessonForm, video_url: up.url }); toast.success('Video uploaded'); }
    catch { toast.error('Upload failed'); }
  };
  const onLessonAttach = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await uploadFile('client_media', f); setLessonForm({ ...lessonForm, attachment_url: up.url, attachment_name: up.original_filename || f.name }); toast.success('Attachment uploaded'); }
    catch { toast.error('Upload failed'); }
  };

  const addLesson = async () => {
    if (!lessonForm.title.trim()) { toast.error('Lesson title required'); return; }
    try {
      await api.post(`/admin/courses/${courseId}/lessons`, { ...lessonForm, sort_order: (data?.lessons?.length || 0) + 1 });
      setLessonForm({ title: '', body: '', video_url: '', attachment_url: '', attachment_name: '' }); load(); toast.success('Lesson added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const delLesson = async (lid) => { if (window.confirm('Delete lesson?')) { await api.delete(`/admin/courses/${courseId}/lessons/${lid}`); load(); } };

  const onWsPdf = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await uploadFile('client_media', f); setWsForm({ ...wsForm, pdf_url: up.url }); toast.success('PDF uploaded'); }
    catch { toast.error('Upload failed'); }
  };

  const addWorksheet = async () => {
    if (!wsForm.title.trim()) { toast.error('Worksheet title required'); return; }
    const payload = { title: wsForm.title, kind: wsForm.kind };
    if (wsForm.kind === 'pdf') payload.pdf_url = wsForm.pdf_url;
    else payload.questions = wsForm.questions.filter(q => q.label.trim());
    try { await api.post(`/admin/courses/${courseId}/worksheets`, payload); setWsForm({ title:'', kind:'fillable', pdf_url:'', questions:[{id:'q1', label:'', type:'text', required:true}] }); load(); toast.success('Worksheet added'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const addQuestion = () => setWsForm({ ...wsForm, questions: [...wsForm.questions, { id: `q${wsForm.questions.length + 1}`, label: '', type: 'text', required: false }] });

  if (!data) return <div className='p-4 text-slate-400'>Loading…</div>;

  return (
    <div>
      <div className='font-display text-xl font-bold mb-1'>{data.course.title}</div>
      <div className='text-xs text-slate-500 mb-5'>{data.course.description}</div>

      {/* Lessons */}
      <div className='mb-6'>
        <h3 className='font-semibold flex items-center gap-2 mb-2'><Film className='w-4 h-4' /> Lessons ({data.lessons.length})</h3>
        <div className='space-y-2 mb-3'>
          {data.lessons.map((l, i) => (
            <div key={l.id} className='p-2 border rounded flex items-center justify-between text-sm' data-testid={`lesson-row-${l.id}`}>
              <span>{i + 1}. {l.title}</span>
              <button onClick={() => delLesson(l.id)} className='text-[#B42318]'><Trash2 className='w-3.5 h-3.5' /></button>
            </div>
          ))}
        </div>
        <Card className='p-3 bg-slate-50 dark:bg-slate-800 space-y-2'>
          <Input placeholder='Lesson title' value={lessonForm.title} onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} data-testid='lesson-title' />
          <Textarea rows={2} placeholder='Lesson text / curriculum notes' value={lessonForm.body} onChange={e => setLessonForm({ ...lessonForm, body: e.target.value })} />
          <div className='flex flex-wrap items-center gap-2 text-xs'>
            <label className='inline-flex items-center gap-1 px-3 py-1.5 rounded border cursor-pointer hover:bg-white dark:hover:bg-slate-700'>
              <Upload className='w-3.5 h-3.5' /> Video <input type='file' accept='video/*' className='hidden' onChange={onLessonVideo} data-testid='lesson-video-input' />
            </label>
            {lessonForm.video_url && <span className='text-emerald-600'>✓ video ready</span>}
            <label className='inline-flex items-center gap-1 px-3 py-1.5 rounded border cursor-pointer hover:bg-white dark:hover:bg-slate-700'>
              <Upload className='w-3.5 h-3.5' /> Worksheet/PDF <input type='file' accept='.pdf,.doc,.docx,image/*' className='hidden' onChange={onLessonAttach} data-testid='lesson-attach-input' />
            </label>
            {lessonForm.attachment_name && <span className='text-emerald-600'>✓ {lessonForm.attachment_name}</span>}
          </div>
          <Button size='sm' onClick={addLesson} className='bg-[#1B3A87] text-white' data-testid='add-lesson-btn'>
            <Plus className='w-3.5 h-3.5 mr-1' /> Add lesson
          </Button>
        </Card>
      </div>

      {/* Worksheets */}
      <div>
        <h3 className='font-semibold flex items-center gap-2 mb-2'><ClipboardList className='w-4 h-4' /> Worksheets ({data.worksheets.length})</h3>
        <div className='space-y-2 mb-3'>
          {data.worksheets.map(w => (
            <div key={w.id} className='p-2 border rounded text-sm flex items-center justify-between'>
              <span>{w.title} <Badge variant='outline' className='ml-1 text-[10px]'>{w.kind}</Badge></span>
            </div>
          ))}
        </div>
        <Card className='p-3 bg-slate-50 dark:bg-slate-800 space-y-2'>
          <Input placeholder='Worksheet title' value={wsForm.title} onChange={e => setWsForm({ ...wsForm, title: e.target.value })} data-testid='ws-title' />
          <div className='flex gap-2 text-xs'>
            <label className='inline-flex items-center gap-1'><input type='radio' checked={wsForm.kind==='fillable'} onChange={() => setWsForm({ ...wsForm, kind: 'fillable' })} /> Fillable form</label>
            <label className='inline-flex items-center gap-1'><input type='radio' checked={wsForm.kind==='pdf'} onChange={() => setWsForm({ ...wsForm, kind: 'pdf' })} /> PDF upload</label>
          </div>
          {wsForm.kind === 'pdf' ? (
            <div>
              <label className='inline-flex items-center gap-1 px-3 py-1.5 rounded border cursor-pointer hover:bg-white dark:hover:bg-slate-700 text-xs'>
                <Upload className='w-3.5 h-3.5' /> Upload printable PDF
                <input type='file' accept='.pdf' className='hidden' onChange={onWsPdf} data-testid='ws-pdf-input' />
              </label>
              {wsForm.pdf_url && <span className='text-emerald-600 text-xs ml-2'>✓ PDF ready</span>}
            </div>
          ) : (
            <div className='space-y-2'>
              {wsForm.questions.map((q, i) => (
                <div key={q.id} className='flex gap-2'>
                  <Input placeholder={`Question ${i + 1}`} value={q.label} onChange={e => { const nq = [...wsForm.questions]; nq[i] = { ...q, label: e.target.value }; setWsForm({ ...wsForm, questions: nq }); }} data-testid={`ws-q-${i}`} />
                  <select value={q.type} onChange={e => { const nq = [...wsForm.questions]; nq[i] = { ...q, type: e.target.value }; setWsForm({ ...wsForm, questions: nq }); }} className='h-10 border rounded px-2 text-xs'>
                    <option value='text'>Text</option>
                    <option value='checkbox'>Checkbox</option>
                    <option value='rating'>Rating</option>
                  </select>
                </div>
              ))}
              <Button size='sm' variant='outline' onClick={addQuestion} className='text-xs'><Plus className='w-3.5 h-3.5 mr-1' /> Add question</Button>
            </div>
          )}
          <Button size='sm' onClick={addWorksheet} className='bg-[#C8102E] text-white' data-testid='add-worksheet-btn'>
            <Plus className='w-3.5 h-3.5 mr-1' /> Add worksheet
          </Button>
        </Card>
      </div>

      {/* Students (enrollments) */}
      <div className='mt-6'>
        <h3 className='font-semibold flex items-center gap-2 mb-2'><Users className='w-4 h-4' /> Students</h3>
        <StudentsPanel courseId={courseId} />
      </div>

      <div className='mt-5 text-right'><Button variant='outline' onClick={onClose}>Close</Button></div>
    </div>
  );
};

/* ---------- StudentsPanel: enroll / unenroll clients ---------- */
const StudentsPanel = ({ courseId }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [er, cr] = await Promise.all([
        api.get(`/admin/courses/${courseId}/enrollments`),
        api.get('/admin/clients'),
      ]);
      setEnrollments(er.data || []);
      setClients(cr.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const enrolledIds = new Set((enrollments || []).map(e => e.student_id));

  const matches = (clients || []).filter(c => {
    if (enrolledIds.has(c.id)) return false;
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (c.full_name || '').toLowerCase().includes(t) || (c.email || '').toLowerCase().includes(t);
  }).slice(0, 8);

  const enroll = async (clientId) => {
    setAdding(true);
    try {
      await api.post(`/admin/courses/${courseId}/enroll`, { student_ids: [clientId] });
      setQ('');
      await load();
      toast.success('Student enrolled');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setAdding(false); }
  };

  const unenroll = async (studentId) => {
    if (!window.confirm('Remove this student from the course?')) return;
    try {
      await api.delete(`/admin/courses/${courseId}/enroll/${studentId}`);
      await load();
      toast.success('Student removed');
    } catch { toast.error('Failed'); }
  };

  return (
    <Card className='p-3 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700' data-testid='students-panel'>
      {/* Enrolled roster */}
      {enrollments.length === 0 ? (
        <div className='text-xs text-slate-500 dark:text-slate-400 mb-3'>No students enrolled yet.</div>
      ) : (
        <div className='space-y-1.5 mb-3'>
          {enrollments.map(e => (
            <div
              key={e.id}
              className='flex items-center justify-between bg-white dark:bg-slate-900 rounded px-3 py-2 text-sm border border-slate-200 dark:border-slate-700'
              data-testid={`enrollment-row-${e.student_id}`}
            >
              <div className='min-w-0'>
                <div className='font-semibold text-slate-800 dark:text-slate-100 truncate'>{e.student_name || 'Unnamed client'}</div>
                <div className='text-xs text-slate-500 dark:text-slate-400 truncate'>{e.student_email}</div>
              </div>
              <button
                onClick={() => unenroll(e.student_id)}
                className='text-[#B42318] hover:text-[#8f1c14] text-xs inline-flex items-center gap-1'
                data-testid={`unenroll-${e.student_id}`}
              >
                <X className='w-3.5 h-3.5' /> Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new */}
      <div className='flex items-center gap-2 mb-2'>
        <div className='relative flex-1'>
          <Search className='w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400' />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder='Search clients by name or email…'
            className='pl-7 h-9'
            data-testid='enroll-search'
          />
        </div>
      </div>
      {q.trim() && (
        <div className='bg-white dark:bg-slate-900 border rounded max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800'>
          {matches.length === 0 ? (
            <div className='px-3 py-2 text-xs text-slate-500'>No matching clients.</div>
          ) : matches.map(c => (
            <button
              key={c.id}
              onClick={() => enroll(c.id)}
              disabled={adding}
              className='w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between disabled:opacity-50'
              data-testid={`enroll-match-${c.id}`}
            >
              <div className='min-w-0'>
                <div className='font-semibold text-slate-800 dark:text-slate-100 truncate'>{c.full_name}</div>
                <div className='text-xs text-slate-500 dark:text-slate-400 truncate'>{c.email}</div>
              </div>
              <UserPlus className='w-3.5 h-3.5 text-[#167A5A] shrink-0' />
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};
